import { useQuery } from '@tanstack/react-query';
import { dashboardAPI } from '../services/api';
import { useOrganization } from './useOrganization';
import { useAuth } from '../context/AuthContext';

export function useSecurityStats() {
  const { canViewOrgData } = useOrganization();
  const { user, organizationId } = useAuth();

  const {
    data,
    isLoading,
    error
  } = useQuery({
    queryKey: ['securityStats', user?.id, organizationId],
    queryFn: async () => {
      // Call your API endpoint to get security metrics.
      const response = await dashboardAPI.getSecurityMetrics(organizationId);
      return response.data;
    },
    enabled: Boolean(organizationId && user?.id),
    select: (responseData) => {
      console.log('Raw security response:', responseData);
      
      // Our API is expected to return an object like:
      // { data: { user_security_stats: { ... }, org_security_stats: { ... } } }
      const userStats = responseData?.data?.user_security_stats;
      const orgStats = responseData?.data?.org_security_stats;

      if (!userStats) {
        console.warn('No user security stats found in response');
        return null;
      }

      // If the backend does not return key_age_days, try to compute it from key_created_at.
      const computedKeyAgeDays =
        userStats.key_age_days != null
          ? Number(userStats.key_age_days)
          : userStats.key_created_at
            ? Math.floor((Date.now() - new Date(userStats.key_created_at).getTime()) / (24 * 60 * 60 * 1000))
            : 0;

      // Transform user security stats
      const userSecurity = {
        security_score: Number(userStats.security_score) || 0,
        key_status: userStats.key_status || 'missing',
        key_age_days: computedKeyAgeDays,
        failed_emails: userStats.failed_emails || 0,
        verification_rate: userStats.verification_rate || 0,
        smtp_configured: userStats.smtp_configured || false,
        total_emails: userStats.total_emails || 0,
        verified_emails: userStats.verified_emails || 0
      };

      // Transform organization security stats if available
      const orgSecurity = orgStats ? {
        security_score: parseFloat(orgStats.security_score) || 0,
        total_users: orgStats.total_users || 0,
        active_users: orgStats.active_users || 0,
        key_configured_users: orgStats.key_configured_users || 0,
        total_domains: orgStats.total_domains || 0,
        verified_domains: orgStats.verified_domains || 0,
        total_emails: orgStats.total_emails || 0,
        verified_emails: orgStats.verified_emails || 0,
        smtp_configured_users: orgStats.smtp_configured_users || 0,
        email_provider: orgStats.email_provider || null,
        email_provider_configured: orgStats.email_provider_configured || false,
        failed_domains: orgStats.failed_domains || 0
      } : null;

      console.log('Transformed security data:', { userSecurity, orgSecurity });

      return { userSecurity, orgSecurity };
    },
    refetchInterval: 30000,
    staleTime: 15000,
    retry: 2
  });

  return {
    userSecurity: data?.userSecurity,
    orgSecurity: data?.orgSecurity,
    isLoading,
    error,
    hasOrgAccess: canViewOrgData
  };
}
